import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import Papa from "papaparse";
import { supabase } from "@/integrations/supabase/client";

interface ExportLearnerCSVProps {
  learnerId: string;
  learnerName: string | null;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}

interface LearnerExportRow {
  "Learner Name": string;
  Email: string;
  "User Type": string;
  "Item Name": string;
  Type: string;
  Status: string;
  "Progress %": string;
  "Enrolled Date": string;
  "Completed Date": string;
  "Certificate Status": string;
  "Assignment Status": string;
  "Due Date": string;
}

export function ExportLearnerCSV({
  learnerId,
  learnerName,
  variant = "outline",
  size = "sm",
}: ExportLearnerCSVProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch learner profile
      const { data: profiles } = await supabase.rpc("admin_search_users_for_deletion", {
        search_query: "",
        include_no_name: true,
      });
      const profile = profiles?.find((p) => p.id === learnerId);

      if (!profile) {
        toast.error("Learner not found");
        return;
      }

      // Fetch all data in parallel
      const [
        curriculumEnrollmentsResult,
        courseEnrollmentsResult,
        curriculumAssignmentsResult,
        courseAssignmentsResult,
        certificatesResult,
        curriculumCoursesResult,
        modulesResult,
        moduleProgressResult,
      ] = await Promise.all([
        supabase
          .from("curriculum_enrollments")
          .select(`
            id, curriculum_id, enrolled_at, completed_at,
            curricula:curriculum_id (title)
          `)
          .eq("user_id", learnerId),
        supabase
          .from("course_enrollments")
          .select(`
            id, course_id, enrolled_at, completed_at,
            courses:course_id (title, curriculum_id)
          `)
          .eq("user_id", learnerId),
        supabase
          .from("curriculum_assignments")
          .select(`
            id, curriculum_id, due_date, completed_at,
            curricula:curriculum_id (title)
          `)
          .eq("user_id", learnerId),
        supabase
          .from("course_assignments")
          .select(`
            id, course_id, due_date, completed_at,
            courses:course_id (title)
          `)
          .eq("user_id", learnerId),
        supabase
          .from("certificates")
          .select("curriculum_id, certificate_number, issued_at")
          .eq("user_id", learnerId),
        supabase.from("curriculum_courses").select("curriculum_id, course_id"),
        supabase.from("modules").select("id, course_id"),
        supabase.from("module_progress").select("module_id, enrollment_id, completed_at"),
      ]);

      const rows: LearnerExportRow[] = [];
      const certificateMap = new Map(
        (certificatesResult.data || []).map((c) => [c.curriculum_id, c])
      );

      // Build assignment lookup maps
      const curriculumAssignmentMap = new Map(
        (curriculumAssignmentsResult.data || []).map((a) => [a.curriculum_id, a])
      );
      const courseAssignmentMap = new Map(
        (courseAssignmentsResult.data || []).map((a) => [a.course_id, a])
      );

      // Process curriculum enrollments
      (curriculumEnrollmentsResult.data || []).forEach((enrollment) => {
        const curriculum = enrollment.curricula as { title: string } | null;
        const certificate = certificateMap.get(enrollment.curriculum_id);
        const assignment = curriculumAssignmentMap.get(enrollment.curriculum_id);

        // Calculate progress
        const coursesInCurriculum =
          curriculumCoursesResult.data?.filter(
            (cc) => cc.curriculum_id === enrollment.curriculum_id
          ) || [];
        const completedCourses = (courseEnrollmentsResult.data || []).filter(
          (ce) =>
            ce.completed_at &&
            coursesInCurriculum.some((cc) => cc.course_id === ce.course_id)
        ).length;
        const totalCourses = coursesInCurriculum.length;
        const progress = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

        rows.push({
          "Learner Name": profile.full_name || "Unknown",
          Email: profile.email || "N/A",
          "User Type": profile.role || "N/A",
          "Item Name": curriculum?.title || "Unknown Curriculum",
          Type: "Curriculum",
          Status: enrollment.completed_at ? "Completed" : progress > 0 ? "In Progress" : "Not Started",
          "Progress %": `${progress}%`,
          "Enrolled Date": format(new Date(enrollment.enrolled_at), "yyyy-MM-dd"),
          "Completed Date": enrollment.completed_at
            ? format(new Date(enrollment.completed_at), "yyyy-MM-dd")
            : "",
          "Certificate Status": certificate ? `Issued (${certificate.certificate_number})` : "Not Earned",
          "Assignment Status": assignment
            ? assignment.completed_at
              ? "Completed"
              : "Assigned"
            : "Not Assigned",
          "Due Date": assignment?.due_date
            ? format(new Date(assignment.due_date), "yyyy-MM-dd")
            : "",
        });
      });

      // Process course enrollments
      (courseEnrollmentsResult.data || []).forEach((enrollment) => {
        const course = enrollment.courses as { title: string; curriculum_id: string | null } | null;
        const assignment = courseAssignmentMap.get(enrollment.course_id);

        // Calculate progress
        const modulesInCourse =
          modulesResult.data?.filter((m) => m.course_id === enrollment.course_id) || [];
        const completedModules = (moduleProgressResult.data || []).filter(
          (mp) =>
            mp.completed_at &&
            mp.enrollment_id === enrollment.id &&
            modulesInCourse.some((m) => m.id === mp.module_id)
        ).length;
        const totalModules = modulesInCourse.length;
        const progress = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

        rows.push({
          "Learner Name": profile.full_name || "Unknown",
          Email: profile.email || "N/A",
          "User Type": profile.role || "N/A",
          "Item Name": course?.title || "Unknown Course",
          Type: "Course",
          Status: enrollment.completed_at ? "Completed" : progress > 0 ? "In Progress" : "Not Started",
          "Progress %": `${progress}%`,
          "Enrolled Date": format(new Date(enrollment.enrolled_at), "yyyy-MM-dd"),
          "Completed Date": enrollment.completed_at
            ? format(new Date(enrollment.completed_at), "yyyy-MM-dd")
            : "",
          "Certificate Status": "N/A",
          "Assignment Status": assignment
            ? assignment.completed_at
              ? "Completed"
              : "Assigned"
            : "Not Assigned",
          "Due Date": assignment?.due_date
            ? format(new Date(assignment.due_date), "yyyy-MM-dd")
            : "",
        });
      });

      if (rows.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Generate CSV
      const csv = Papa.unparse(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = (learnerName || "learner").replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${safeName}_learning_progress_${format(new Date(), "yyyy-MM-dd")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Learner progress exported!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export learner data");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button variant={variant} size={size} onClick={handleExport} disabled={isExporting}>
      {isExporting ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      Export CSV
    </Button>
  );
}
